// ─── Enums / Literals ─────────────────────────────────────────────────────────

export type ReconStatus =
  | "MATCHED"
  | "MISMATCH_PRICE"
  | "MISMATCH_QUANTITY"
  | "MISMATCH_MARKUP"
  | "MISMATCH_PRICE_AND_MARKUP"
  | "UNMATCHED_BGM"
  | "UNMATCHED_ACERS"
  | "CANON_FAILED"
  | "FAILED_MISSING_CONTRACT_MAPPING"
  | "FAILED_MISSING_CLIENT_MAPPING"
  | "SUCCESS"
  | "SOURCE_ISSUE"
  | "FAILURE";


export const ReconStatusEnum = {
  MATCHED: "MATCHED",
  MISMATCH_PRICE: "MISMATCH_PRICE",
  MISMATCH_QUANTITY: "MISMATCH_QUANTITY",
  MISMATCH_MARKUP: "MISMATCH_MARKUP",
  MISMATCH_PRICE_AND_MARKUP: "MISMATCH_PRICE_AND_MARKUP",
  UNMATCHED_BGM: "UNMATCHED_BGM",
  UNMATCHED_ACERS: "UNMATCHED_ACERS",
  CANON_FAILED: "CANON_FAILED",
  FAILED_MISSING_CONTRACT_MAPPING: "FAILED_MISSING_CONTRACT_MAPPING",
  FAILED_MISSING_CLIENT_MAPPING: "FAILED_MISSING_CLIENT_MAPPING",
  SUCCESS: "SUCCESS",
} as const satisfies Record<string, ReconStatus>;

export type SideLabel = "ACERS" | "BGM" | "Britannia";

export const ReconSourceEnum = {
  ACERS: "ACERS",
  BGM: "BGM",
} as const satisfies Record<string, SideLabel>;

export type BuySell = "BUY" | "SELL";

export type ActiveTab =
  | "all"
  | "matched"
  | "mismatches"
  | "unmatched"
  | "failures"
  | "sourceIssue";

export const ACCEPTED_EXTENSIONS = [".xlsx", ".xlsm", ".csv"];

export const ActiveTabEnum = {
  ALL: "all",
  MATCHED: "matched",
  MISMATCHES: "mismatches",
  UNMATCHED: "unmatched",
  FAILURES: "failures",
  SOURCE_ISSUE: "sourceIssue"
} as const satisfies Record<string, ActiveTab>;

export interface BreakdownReasonDetail {
  row?: number;
  value?: string;
  reasons: string[];
  acers_quantity?: number | null;
  bgm_quantity?: number | null;
  acers_price?: number | null;
  bgm_price?: number | null;
}


// ─── Filter / Request ─────────────────────────────────────────────────────────

export interface ReconRunPayload {
  source: string;
  start_date: string; // "YYYY-MM-DD"
  end_date: string;   // "YYYY-MM-DD"
  file: File;
  contract?: string;
  buy_sell?: BuySell;
}

export interface ReconReRunPayload {
  source: string;
  start_date: string;
  end_date: string;
  contract?: string;
  buy_sell?: BuySell;
  // No file — reuses previously uploaded file on backend
}

// ─── Filter Form State ────────────────────────────────────────────────────────

export interface ReconFilterFormData {
  source: string;
  start_date: string;
  end_date: string;
  contract: string;
  buy_sell: string;
}

// ─── Summary ──────────────────────────────────────────────────────────────────

export interface ReconSummary {
  total_bgm_trades: number;
  total_acers_trades: number;
  matched: number;
  mismatch_price: number;
  mismatch_quantity: number;
  mismatch_price_and_quantity: number;
  unmatched_bgm: number;
  unmatched_acers: number;
  canon_failed: number;
}

export interface BreakReasonItem {
  row?: number;
  value?: string;
  reason: string;
  bgm_price?: number | null;
  acers_price?: number | null;
  bgm_quantity?: number | null;
  acers_quantity?: number | null;
}

// ─── NEW: flat per-side trade row, as returned by the backend ──────────────
// The backend now returns two separate flat arrays (results.acers /
// results.bgm) instead of one merged/grouped array. Rows on both sides
// that belong together share the same `group_id`.

export interface ReconTradeItem {
  group_id: string;
  trade_date: string | null;
  client: string | null;
  contract: string | null;
  direction: string;
  contract_month: string | null;
  quantity: number | null;
  price: number | null;
  status: ReconStatus;
  reasons: BreakReasonItem[];
  has_multiple_rows?: boolean;
  // Only present on BGM-side rows today, but kept optional so either side
  // can carry it if the backend adds it later.
  canonicalisation_status?: string[];
  // Optional: individual underlying trade IDs that were aggregated into this
  // group_id row (Major/Minor rollup). When absent or length <= 1, the row
  // has no aggregation and the expand icon / breakdown are not shown.
  trade_ids?: string[];
}

// ─── Aggregation Breakdown (expand-on-click for a Group ID) ────────────────
// Fetched on demand when the user expands a row whose trade_ids.length > 1.
export interface GroupBreakdownTrade {
  source_trade_id: string | null;
  source_trade_date: string | null;
  trade_date: string | null;
  source_client_value: string | null;
  client: string | null;
  source_contract_symbol: string | null;
  contract: string | null;
  source_direction_value: string | null;
  direction: string;
  source_contract_month: string | null;
  contract_month: string | null;
  source_quantity_value: string | null; // raw lots as sent by the source, e.g. "-5"
  tick: number | null;
  quantity: number | null; // canonical qty = lots * tick
  price: number | null;
  canonicalisation_status: ReconStatus;
  reconciliation_status: ReconStatus;
  matched_with_id: string | null;
  source_system: SideLabel; // "ACERS" | "Britannia"
  break_reason: BreakdownReasonDetail[];
}

export interface GroupBreakdownResponse {
  group_id: string;
  status: ReconStatus;
  acers_quantity_sum: number | null;
  bgm_quantity_sum: number | null;
  acers_avg_price: number | null;
  bgm_avg_price: number | null;
  acers_trades: GroupBreakdownTrade[];
  bgm_trades: GroupBreakdownTrade[];
}

// ─── NEW: API response shape — separated by source ─────────────────────────

export interface ReconResultsBySide {
  acers: ReconTradeItem[];
  bgm: ReconTradeItem[];
}

export interface ReconRunResponse {
  message: string;
  results: ReconResultsBySide;
}

// ─── NEW: client-side combined view (one entry per group_id) ───────────────
// Built in the page from results.acers + results.bgm. Used only to compute
// tab counts / summary numbers / failure detection — never rendered directly.

export interface ReconCombinedGroup {
  group_id: string;
  status: ReconStatus;
  acers?: ReconTradeItem;
  bgm?: ReconTradeItem;
}

export interface SelectOption {
  value: string;
  label: string;
}

// ─── Legacy flat-trade types (kept for backward compatibility elsewhere) ───
export interface ReconTradeRow {
  id: string;
  trade_id: string;
  source_trade_id: string | null;
  trade_date: string;
  source_system: string;
  client: string;
  contract: string;
  contract_month: string;
  direction: string;
  price: number;
  quantity: number;
  bgm_price: number | null;
  acers_price: number | null;
  markup_bgm: number | null;
  markup_acers: number | null;
  reconciliation_status: ReconStatus;
  canonicalisation_status: ReconStatus;
  break_reason: BreakReasonItem[] | string | string[] | null;
  edit_id: string | null;
  issue?: string;
  matched_with_id?: string;
}

export interface SidedTableConfig {
  label: string;
  accent: string;
  bg: string;
  border: string;
  systemFilter: (r: ReconTradeRow) => boolean;
}
